import cn from 'classnames';
import AmountSelect from 'components/amount-select/amount-select-test';
import Price from 'components/price';
import { EffectiveCart } from 'interfaces/cart';
import { isGiftProductItem, isSubscriptionItem } from 'interfaces/line-item';
import { FormattedMessage } from 'react-intl';
import { toCartItem } from 'contexts/cart/utils/to-cart-item';
import { isRedemptionCartItemInput } from 'contexts/type/cart-type';
import { useCart } from 'contexts/cart';

interface CartItemDescriptionProps {
  item: EffectiveCart['items'][0];
  onProductQuantityChange: (quantity: number) => void | Promise<void>;
  canChangeAmount: boolean;
}

const CartItemDescription: React.FC<CartItemDescriptionProps> = ({ item, canChangeAmount }) => {
  const { updateCart } = useCart();

  return (
    <div className="mt-1 flex flex-col justify-between sm:mb-2 lg:flex-row">
      <div>
        <div data-test="product-variantTitle" className="text-text-light-bg-secondary pt-1 text-sm">
          {item.variantTitle}
        </div>
      </div>

      <div className="flex flex-col justify-end lg:flex-row lg:items-center">
        <div
          data-test="AmountSelect"
          className={cn(
            'flex w-28 flex-row items-center sm:w-36 lg:items-start',
            canChangeAmount ? 'justify-between lg:justify-end' : 'justify-end'
          )}
        >
          {item.productType !== 'Testpaket' && canChangeAmount && (
            <div className="mr-2 w-14 lg:mr-4">
              <AmountSelect
                value={item.quantity}
                onChange={(quantity) => {
                  if (!isGiftProductItem(item)) {
                    updateCart([
                      {
                        target: toCartItem({ item, shouldQuantity: false }),
                        updateData: {
                          variantId: String(item.variantId),
                          quantity,
                        },
                      },
                    ]);
                  } else if (isRedemptionCartItemInput(item)) {
                    updateCart([
                      {
                        target: toCartItem({ item, shouldQuantity: false }),
                        updateData: {
                          variantId: String(item.variantId),
                          quantity: quantity,
                          points: item.points,
                          redeemItem: true,
                        },
                      },
                    ]);
                  } else {
                    throw new Error('unexpected usage of CartItemDescription');
                  }
                }}
              />
            </div>
          )}
        </div>
        <div
          data-test="cart-Price"
          className="text-primary-main mt-2 flex flex-row-reverse items-center text-right text-lg leading-none font-medium sm:w-24 lg:mt-0 lg:w-16 lg:flex-col lg:pt-2"
        >
          <span
            className={cn(
              isSubscriptionItem(item) || item.compareAtPrice
                ? 'text-text-light-bg-discounted-price font-black sm:font-medium'
                : 'text-primary-main'
            )}
          >
            {!isGiftProductItem(item) ? (
              <Price value={item.price ?? item.defaultPrice} cents />
            ) : (
              <div className="text-base lg:text-lg">
                <FormattedMessage id="account:orders:point" values={{ points: item.points }} />
              </div>
            )}
          </span>
          {item.compareAtPrice !== item.price && item.compareAtPrice && item.compareAtPrice > 0 && (
            <span
              className={
                'text-grey-900 relative mr-1 block text-xs leading-none font-medium line-through sm:mr-0 lg:text-sm'
              }
            >
              <Price value={item.compareAtPrice} cents />
            </span>
          )}

          {isSubscriptionItem(item) &&
            item.interval &&
            item.defaultPrice !== item.price &&
            !item.compareAtPrice && (
              <span
                className={
                  'text-grey-900 relative block text-xs leading-none font-medium line-through lg:text-sm'
                }
              >
                <Price value={item.price ?? item.subscriptionPrice} cents />
              </span>
            )}
        </div>
      </div>
    </div>
  );
};

export default CartItemDescription;
